import { callCloudRun } from './cloudRun';

/**
 * Survey funnel events for Coach Portal -> Survey Progress: who opened the
 * shared survey link before signing in, how far into the survey a runner got,
 * and who clicked "Maybe Later". Every call is fire-and-forget — tracking must
 * never get in the way of answering the survey.
 */

const BASE_URL = process.env.REACT_APP_CLOUD_RUN_URL ?? '';
const DEVICE_KEY = 'rhwb-survey-device';

/** The survey link, including the Season 15 address that redirects to it. */
export function isSurveyPath(pathname: string): boolean {
  return pathname === '/survey' || pathname === '/season15-survey';
}

/**
 * The funnel step for a survey page step: questions 0-3 are steps 1-4; the
 * confirmation screen (4) is not a step — the response itself records that.
 */
export function surveyStepNumber(step: number): number | null {
  return Number.isInteger(step) && step >= 0 && step <= 3 ? step + 1 : null;
}

function randomId(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * A random id for this browser, so one person reopening the link counts once
 * a day. It identifies a browser, never a person, and is sent nowhere else.
 */
export function surveyDeviceId(): string {
  try {
    const saved = localStorage.getItem(DEVICE_KEY);
    if (saved && /^[a-f0-9]{32}$/.test(saved)) return saved;
    const id = randomId();
    localStorage.setItem(DEVICE_KEY, id);
    return id;
  } catch {
    return randomId(); // storage blocked: counts this page load on its own
  }
}

let linkOpenSent = false;

/** The survey link was opened by someone not signed in. Once per page load. */
export function recordSurveyLinkOpen(): void {
  if (linkOpenSent || !BASE_URL) return;
  linkOpenSent = true;
  fetch(`${BASE_URL}/api/survey-funnel/open`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ device_id: surveyDeviceId() }),
    keepalive: true,
  }).catch(() => { /* tracking only */ });
}

/** A signed-in runner reached a survey step, clicked "Maybe Later", or saw a submit fail. */
export function recordSurveyEvent(event: 'step' | 'maybe_later' | 'submit_failed', step?: number): void {
  callCloudRun('/api/survey-funnel/event', event === 'step' ? { event, step } : { event })
    .catch(() => { /* tracking only */ });
}
